import React, { useEffect, useMemo, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, Box, CircularProgress, Typography } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import SurveyForm from './pages/SurveyForm';
import SurveysPage from './pages/SurveysPage';
import SurveyDetailsPage from './pages/SurveyDetailsPage';
import FarmersPage from './pages/FarmersPage';
import ParticipantsPage from './pages/ParticipantsPage';
import DiseasePestsPage from './pages/DiseasePestsPage';
import ImportSurveysPage from './pages/ImportSurveysPage';
import ReferenceDataPage from './pages/ReferenceDataPage';
import SeedReportsPage from './pages/SeedReportsPage';
import ReportsPage from './pages/ReportsPage';
import { useTranslation } from 'react-i18next';
import './i18n';
import './styles/rtl.css';

const SettingsPage = () => {
  const { t } = useTranslation();
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>{t('settings')}</Typography>
      <Typography>{t('pageUnderDevelopment')}</Typography>
    </Box>
  );
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route 
        path="/login" 
        element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />
        } 
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/surveys"
        element={
          <ProtectedRoute>
            <Layout>
              <SurveysPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/farmers"
        element={
          <ProtectedRoute>
            <Layout>
              <FarmersPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/participants"
        element={
          <ProtectedRoute>
            <Layout>
              <ParticipantsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/disease-pests"
        element={
          <ProtectedRoute>
            <Layout>
              <DiseasePestsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/import-surveys"
        element={
          <ProtectedRoute>
            <Layout>
              <ImportSurveysPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reference-data"
        element={
          <ProtectedRoute>
            <Layout>
              <ReferenceDataPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <Layout>
              <ReportsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/seed-reports"
        element={
          <ProtectedRoute>
            <Layout>
              <SeedReportsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/survey/new"
        element={
          <ProtectedRoute>
            <Layout>
              <SurveyForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/survey/edit/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <SurveyForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/survey/details/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <SurveyDetailsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <SettingsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

// This component contains the main app logic and is wrapped in Suspense
function AppContent() {
  const { i18n } = useTranslation();

  // Effect to update document direction and lang attributes
  useEffect(() => {
    const direction = i18n.dir();
    document.dir = direction;
    document.documentElement.dir = direction;
    document.documentElement.lang = i18n.language;
    
    // Force re-render of Material-UI components
    document.body.setAttribute('dir', direction);
  }, [i18n, i18n.language]);

  // Create a theme that dynamically updates based on the language direction
  const theme = useMemo(
    () =>
      createTheme({
        direction: i18n.dir(),
        palette: {
          primary: {
            main: '#4CAF50',
            light: '#81C784',
            dark: '#2E7D32',
          },
          secondary: {
            main: '#FF9800',
            light: '#FFB74D',
            dark: '#F57C00',
          },
          background: {
            default: '#f5f5f5',
          },
        },
        typography: {
          fontFamily: i18n.dir() === 'rtl'
            ? '"Cairo", "Roboto", "Helvetica", "Arial", sans-serif'
            : '"Roboto", "Helvetica", "Arial", sans-serif',
        },
      }),
    [i18n.language] // Re-create theme only when language changes
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

// The main App component wraps everything in Suspense
function App() {
  return (
    <Suspense fallback={
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    }>
      <AppContent />
    </Suspense>
  );
}

export default App;
